import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography, Divider } from '@material-ui/core';
import CustomTable from 'components/CustomTable';
import { Remove as RemoveIcon, Add as AddIcon } from '@material-ui/icons';
import { basicDefaultFormData, advancedDefaultFormData } from 'hooks/search/searchData';
import useApplicationTableData from 'hooks/useApplicationData';
import usePostApprovalData from 'hooks/usePostApprovalData';
import CustomButton from 'components/CustomInput/Button/index';
import ApplicationColumnButton from 'components/ColumnSelectButton/ApplicationColumnButton';
import PostApprovalColumnButton from 'components/ColumnSelectButton/PostApprovalColumnButton';
import PageSize from 'components/CustomTable/PageSize';
import styled from 'styled-components';
import Filter from 'components/CustomTable/Filter';
import CustomPagination from 'components/CustomPagination';
import DetailDrawer from 'components/PostApproval/DetailDrawer';
import SideBar from 'components/PostApproval/SideBar';
import { GET_SEARCH_LIST, SET_SEARCH_DATA, SET_SELECT_TEMPLATE_BOX } from 'store/key';
import SearchCriteria from './SearchCriteria';
import ButtonsContent from './ButtonsContent';
import { setGlobal } from 'store/global/reducer';
import { useLocation } from 'react-router';
import { statusKeyToDisplay } from 'utility/lookup';
import { searchDocTypeEnum } from 'hooks/search/searchData';
import { SelectTemplateDialog } from './SelectTemplateDialog';

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(2),
        paddingTop: 16,
        overflow: 'hidden',
    },
    resultBox: {
        marginTop: theme.spacing(2),
    },
    item: {
        marginTop: 12,
    },
    basicTitle: {
        marginTop: 4,
        marginBottom: -4,
        fontSize: 14,
    },
    advancedTitle: {
        marginTop: 12,
    },
    criteriaBox: {
        paddingTop: theme.spacing(2),
    },
    criteriaContent: {
        paddingLeft: theme.spacing(2),
    },
    resultContent: {
        paddingLeft: theme.spacing(2),
        paddingTop: theme.spacing(1),
    },
}));

const SFilterLayout = styled.div`
    display: flex;
    margin: 30px 0 10px 0;
`;

const SPaginationLayout = styled.div`
    margin-top: 20px;
`;

const SLayout = styled.div`
    display: flex;
`;

const Search = (props) => {
    const location = useLocation();
    const folder = location.pathname.split('/post-approval-document/')?.[1];

    const classes = useStyles();
    const dispatch = useDispatch();

    const hospitals = useSelector((state) => state.system.hospitals);
    const userInfo = useSelector((state) => state.user);
    const searchData = useSelector((state) => state.search);
    const { isApplication, applicationRows, postApprovalRows, isLoading, forceErrorDisplay, docType, basicFormData, filterKeyword, selectedRows, advancedFormData, showCriteria, isFullViewShow, expandCount, applicationPage, postApprovalPage } = searchData;

    const { isShow, handleConfirm } = useSelector(state => state.search.selectTemplateBox);
    const onClose = () => {
        dispatch({
            type: SET_SELECT_TEMPLATE_BOX,
            payload: {
                selectTemplateBox: {
                    isShow: false
                }
            }
        });
    }

    const getPage = () => {
        if (isApplication) {
            return applicationPage.page;
        } else {
            return postApprovalPage.page;
        }
    }
    const getPageSize = () => {
        if (isApplication) {
            return applicationPage.pageSize;
        } else {
            return postApprovalPage.pageSize;
        }
    }
    const getSort = () => {
        if (isApplication) {
            return applicationPage.sort;
        } else {
            return postApprovalPage.sort;
        }
    }
    const getIsDec = () => {
        if (isApplication) {
            return applicationPage.isDesc;
        } else {
            return postApprovalPage.isDesc;
        }
    }
    const getTotal = () => {
        if (isApplication) {
            return applicationPage.total;
        } else {
            return postApprovalPage.total;
        }
    }

    const loadList = (newPageSize, newPage, newSort, newDesc) => {
        let page = getPage(), pageSize = getPageSize(), sort = getSort(), isDesc = getIsDec();

        if (isValid()) {
            const data = {
                role: userInfo.role,
                page: newPage ?? page,
                pageSize: newPageSize ?? pageSize,
                sort: newSort ?? sort,
                isDesc: newDesc ?? isDesc,
            };
            dispatch({
                type: GET_SEARCH_LIST,
                payload: data,
            });
        }
    };

    const [displayItem, setDisplayItem] = useState(null);
    const { columns: applicationColumns } = useApplicationTableData({ onSortFunc: loadList });
    const { columns: postApprovalColumns } = usePostApprovalData({ setDisplayItem, onSortFunc: loadList, folder });
    const columns = isApplication ? applicationColumns : postApprovalColumns;
    const searchRefreshKey = useSelector((state) => state.global.searchRefreshKey);
    const resultRows = (isApplication ? applicationRows : postApprovalRows) ?? [];

    const formValid = useRef({});

    const setExpandCount = (expandCount) => {
        dispatch({
            type: SET_SEARCH_DATA,
            payload: {
                expandCount: expandCount,
            },
        });
    }
    const setBasicFormData = (newData) => {
        dispatch({
            type: SET_SEARCH_DATA,
            payload: {
                basicFormData: newData,
            },
        });
    }

    const getClusterList = () => {
        if (hospitals.length) {
            const list = hospitals.map((item) => {
                item.key = item.clusterCode;
                item.label = item.clusterName;
                return item;
            });

            return {
                ...advancedDefaultFormData,
                applyingCluster: {
                    ...advancedDefaultFormData.applyingCluster,
                    operators: list,
                },
            };
        } else {
            return advancedDefaultFormData;
        }
    };

    const setAdvancedFormData = (advancedFormData) => {
        dispatch({
            type: SET_SEARCH_DATA,
            payload: {
                advancedFormData: advancedFormData,
            },
        });
    }
    const defaultFormData = {
        basic: {
            formData: basicFormData,
            setFormData: (e) => {
                const { name, value } = e.target;
                setBasicFormData({
                    ...basicFormData,
                    [name]: { ...basicFormData[name], value: value },
                });
            },
        },
        advanced: {
            formData: advancedFormData,
            setFormData: (e) => {
                const { name, value } = e.target;
                setAdvancedFormData({
                    ...advancedFormData,
                    [name]: { ...advancedFormData[name], value: value },
                });
            },
        },
    };


    const sideBarApplicationId = useSelector((state) => state.global.sideBarApplicationId);


    const setSelectedRows = (selectedRows) => {
        dispatch({
            type: SET_SEARCH_DATA,
            payload: {
                selectedRows: selectedRows,
            },
        });
    }
    const setFilterKeyword = (filterKeyword) => {
        dispatch({
            type: SET_SEARCH_DATA,
            payload: {
                filterKeyword: filterKeyword,
            },
        });
    }

    const setPageSize = (pageSize) => {
        const fieldText = isApplication ? 'applicationPage' : 'postApprovalPage';
        const oldPage = isApplication ? applicationPage : postApprovalPage;
        dispatch({
            type: SET_SEARCH_DATA,
            payload: {
                [fieldText]: {
                    ...oldPage,
                    pageSize: pageSize,
                },
            },
        });

    }
    const setPage = (page) => {
        const fieldText = isApplication ? 'applicationPage' : 'postApprovalPage';
        const oldPage = isApplication ? applicationPage : postApprovalPage;
        dispatch({
            type: SET_SEARCH_DATA,
            payload: {
                [fieldText]: {
                    ...oldPage,
                    page: page,
                },
            },
        });
    }
    const setShowCriteria = (showCriteria) => {
        dispatch({
            type: SET_SEARCH_DATA,
            payload: {
                showCriteria: showCriteria,
            },
        });
    }
    const setFullViewShow = (isFullViewShow) => {
        dispatch({
            type: SET_SEARCH_DATA,
            payload: {
                isFullViewShow: isFullViewShow,
            },
        });
    }

    const isDetailBack = useSelector((state) => state.global.isDetailBack);

    useEffect(() => {
        if (!isDetailBack) {
            resetData();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // when searchRefreshKey changed, refresh the search list
    const isSearchRef = useRef(null);
    useEffect(() => {
        if (isDetailBack || (docType && isSearchRef.current)) {
            loadList()
        }
        isSearchRef.current = true;
        dispatch(
            setGlobal({
                isDetailBack: false
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchRefreshKey])

    const resetData = () => {

        setDisplayItem(null);
        dispatch({
            type: SET_SEARCH_DATA,
            payload: {
                forceErrorDisplay: false,
                docType: '',
                conditionsRequests: [],
                applicationRows: [],
                postApprovalRows: [],
                isApplication: true,

                basicFormData: basicDefaultFormData,
                applicationPage: {
                    pageSize: 10,
                    page: 1,
                    sort: 'updateDtm',
                    isDesc: true,
                    total: 0,
                },
                postApprovalPage: {
                    pageSize: 10,
                    page: 1,
                    sort: 'updateDtm',
                    isDesc: true,
                    total: 0,
                },
                selectedRows: [],
                filterKeyword: '',
                showCriteria: true,
                isFullViewShow: false,
                advancedFormData: getClusterList(),
                expandCount: 0
            },
        });
    };

    const isValid = () => {
        if (!forceErrorDisplay) {
            dispatch({
                type: SET_SEARCH_DATA,
                payload: {
                    forceErrorDisplay: true,
                },
            });
        }

        return !checkFormValid();
    };

    const getFilterResultApplication = () => {
        const rows = isApplication ? applicationRows : postApprovalRows;
        if (!filterKeyword) {
            return rows;
        }
        const filtered = rows.filter((item) => {
            let isFiltered = false;

            Object.values(item).forEach((value) => {
                if (typeof value === 'string') {
                    if (value.toLowerCase().includes(filterKeyword.toLowerCase())) {
                        isFiltered = true;
                    }
                }
            });
            if (item.statusId || item.statusId === 0) {
                if (statusKeyToDisplay(item.statusId).toLowerCase().includes(filterKeyword.toLowerCase())) {
                    isFiltered = true;
                }
            }
            if (item.singleCluster === true) {
                if ('Single'.toLowerCase().includes(filterKeyword.toLowerCase())) {
                    isFiltered = true;
                }
            } else if (item.singleCluster === false) {
                if ('Multiple'.toLowerCase().includes(filterKeyword.toLowerCase())) {
                    isFiltered = true;
                }
            }

            return isFiltered;
        });
        return filtered;
    };

    const onSelectionModelChange = (rows) => {
        setSelectedRows(rows);
    };
    const changeDisplayCriteria = () => {
        setShowCriteria(!showCriteria);
    };

    const checkFormValid = () => {
        const error = Object.keys(formValid.current).some((key) => !formValid.current[key]);
        return error;
    };

    const changePageSize = async (value) => {
        setPageSize(value);
        loadList(value);
    };

    const changePage = (value) => {
        setPage(value);
        loadList(getPageSize(), value);
    };
    return (
        <div>
            <DetailDrawer
                onFullViewShow={(isShow) => setFullViewShow(isShow)}
                isShow={isFullViewShow}
                onClose={() => setFullViewShow(false)}
            />
            <SLayout>
                <Grid container className={classes.container} wrap="nowrap" direction="column" style={{ flexGrow: 1 }}>
                    <Grid>
                        <ButtonsContent
                            userRole={userInfo.role}
                            loadList={loadList}
                            selectedRows={selectedRows}
                            resetData={resetData}
                            searchData={searchData}
                            isValid={isValid}
                            resultRows={resultRows}
                        />
                        <Grid container className={classes.criteriaBox}>
                            <CustomButton variant="outlined" onClick={changeDisplayCriteria}>
                                {showCriteria ? <RemoveIcon /> : <AddIcon />}
                                <Typography color="primary" style={{ fontWeight: 'bold', fontSize: 14 }}>
                                    Search Criteria
                                </Typography>
                            </CustomButton>
                            <Divider style={{ width: '100%', marginTop: 6 }} />
                        </Grid>
                        {showCriteria && (
                            <SearchCriteria
                                classes={classes}
                                searchData={searchData}
                                formValid={formValid}
                                defaultFormData={defaultFormData}
                                basicFormData={basicFormData}
                                advancedFormData={advancedFormData}
                            />
                        )}
                    </Grid>
                    <Grid container className={classes.resultBox}>
                        <Grid container>
                            <Typography color="primary" style={{ fontWeight: 'bold', fontSize: 14 }}>
                                Search Results
                            </Typography>
                            <Divider style={{ width: '100%' }} />
                        </Grid>
                        <div style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ margin: '10px 0', display: 'flex' }}>
                                    {isApplication ? <ApplicationColumnButton /> : <PostApprovalColumnButton />}
                                    {(docType === searchDocTypeEnum.INIT_APPLICATION && getFilterResultApplication().length > 0) && <><CustomButton style={{ marginLeft: 15, marginRight: 15 }} onClick={() => setExpandCount(-1 - Math.random())}>
                                        Collapse All
                                    </CustomButton>
                                        <CustomButton onClick={() => setExpandCount(1 + Math.random())}>
                                            Expand All
                                        </CustomButton>
                                    </>}
                                </div>

                                <SFilterLayout>
                                    <Filter
                                        onChange={(value) => {
                                            setFilterKeyword(value);
                                        }}
                                        value={filterKeyword}
                                    />
                                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: 10 }}>
                                        <PageSize label={"Page Size"} value={getPageSize()} onChange={changePageSize} />
                                    </div>
                                </SFilterLayout>
                            </div>

                            <CustomTable
                                key={basicFormData?.documentType?.value}
                                isCheckboxEnable
                                disabledHeaderCheckbox={true}
                                disabledItemCheckbox={selectedRows.length > 0}
                                isExpandRow={isApplication}
                                columns={columns}
                                rows={getFilterResultApplication()}
                                allowResize
                                onRowSelected={(row) => onSelectionModelChange(row)}
                                loading={isLoading}
                                isExpandAll={expandCount}
                            />

                            <SPaginationLayout>
                                <CustomPagination
                                    page={getPage() || 1}
                                    pageSize={getPageSize() || 10}
                                    total={getTotal() || 0}
                                    isLoading={isLoading}
                                    onPageChange={changePage}
                                />
                            </SPaginationLayout>
                        </div>
                    </Grid>
                </Grid>
                {displayItem && (
                    <div style={{ borderLeft: '1px solid #dcdcdc' }}>
                        <SideBar
                            displayItem={displayItem}
                            setDisplayItem={setDisplayItem}
                            onFullViewShow={(isShow) => setFullViewShow(isShow)}
                        />
                    </div>
                )}

                {sideBarApplicationId && (
                    <div style={{ borderLeft: '1px solid #dcdcdc' }}>
                        <SideBar onFullViewShow={(isShow) => setFullViewShow(isShow)} />
                    </div>
                )}
            </SLayout>
            <SelectTemplateDialog
                isShow={isShow}
                maxWidth="sm"
                // user={user}
                onClose={onClose}
                handleConfirm={handleConfirm}
            />
        </div>
    );
};

export default Search;
